import React, { useContext, useEffect, useState } from "react";
import { AppContext } from "../../../AppContext";
import useMasterData from "../../Hooks/useMasterData";
import { ROLES } from "../../../Routes/routes";
import { useSchoolData } from "../../../libs/data-access";
import { showToast } from "../../../utils/utility";
import moment from "moment";

const useDailyStudentAttendance = () => {
  const DEFAULT_ITEMS_PER_PAGE = 10;
  const { user, role } = useContext(AppContext);
  const {
    districts,
    getDistrictByStateId,
    blocks,
    getBlocksByDistrictId,
    classes,
    getClasses,
  } = useMasterData();

  const { fetchSchoolData, postSchoolData } = useSchoolData();

  const [district, setDistrict] = React.useState<any>(undefined);
  const [block, setBlock] = React.useState<any>(undefined);
  const [cluster, setCluster] = React.useState<any>(undefined);
  const [school, setSchool] = React.useState<any>(undefined);
  const [schools, setSchools] = React.useState<any>([]);
  const [clusters, setClusters] = React.useState<any>([]);
  const [status, setStatus] = React.useState<any>(undefined);
  const [grade, setGrade] = React.useState<any>(undefined);
  const [date, setDate] = React.useState<any>(undefined);
  const [loading, setLoading] = React.useState<boolean>(false);
  const [data, setData] = React.useState<any>(undefined);
  const [allData, setAllData] = React.useState<any>(undefined);
  const [currentPage, setCurrentPage] = useState(0);
  const [itemsPerPage, setItemsPerPage] = useState(DEFAULT_ITEMS_PER_PAGE);

  const [totalRecords, setTotalRecords] = useState(10);
  const [search, setSearch] = React.useState<string>("");
  const nextPage = () => setCurrentPage(currentPage + 1);
  const prevPage = () => setCurrentPage(currentPage - 1);

  const fetchClusterSchools = async (block: number) => {
    setClusters([]);
    const response = await fetchSchoolData(
      `/GetClusterSchoolListByBlockId/${block}`,
    );
    if (response.isSuccess) {
      // console.log("Response for fetch cluster schools: ", response.data);
      setClusters(response.data);
    } else {
      console.error("Error while fetching cluster schools: ", response.error);
      showToast({
        type: "error",
        title: "Something Went Wrong!",
        message: response.message || "Could not fetch cluster schools.",
      });
    }
  };

  const fetchSchools = async (clusterSchool: number) => {
    setSchools([]);
    const response = await fetchSchoolData(
      `/GetSchoolListByClusterSchoolId/${clusterSchool}`,
    );
    if (response.isSuccess) {
      // console.log("Response for fetch schools: ", response.data);
      setSchools(response?.data);
    } else {
      console.error("Error while fetching schools: ", response.error);
      showToast({
        type: "error",
        title: "Something Went Wrong!",
        message: response.message || "Could not fetch schools.",
      });
    }
  };

  const getAttendanceData = async () => {
    setLoading(true);
    let payload: any = {
      attendanceDate: moment(date)?.format('YYYY-MM-DD'),
      schoolCode: String(school),
      classId: grade,
      status: Number(status),
    };
     const res = await postSchoolData(`/GetAttendenceDetailBySchool`,payload);
     if (res.isSuccess) {
       setCurrentPage(0);
       setAllData(res?.data);
       setItemsPerPage(DEFAULT_ITEMS_PER_PAGE);
       let data = res?.data?.studentDetails?.slice(0, itemsPerPage);
       setData(data);
       setTotalRecords(res?.data?.studentDetails?.length);
     } else {
       setAllData([]);
       setData([]);
       showToast({
         type: "error",
         message: res.message || "Something went wrong",
       });
     }

    setLoading(false);
  };

  useEffect(() => {
    getDistrictByStateId(1);
    getClasses();
    if (role?.includes(ROLES.BLOCK)) {
      setDistrict(user?.details?.blockUserDetails?.district_Id);
      setBlock(user?.details?.blockUserDetails?.block_Id);
    }
    if (role?.includes(ROLES.SCHOOL)) {
      setDistrict(user?.details?.schoolUserDetails?.district_Id);
      setBlock(user?.details?.schoolUserDetails?.block_Id);
      setCluster(user?.details?.schoolUserDetails?.cluster_School_Id);
      setSchool(user?.details?.schoolUserDetails?.school_Code);
    }
    if (role?.includes(ROLES.DISTRICT)) {
      setDistrict(user?.details?.districtUserDetails?.district_Id);
    }
  }, []);

  useEffect(() => {
    district && getBlocksByDistrictId(district);
  }, [district]);

  useEffect(() => {
    block && fetchClusterSchools(block);
  }, [block]);

  useEffect(() => {
    cluster && fetchSchools(cluster);
  }, [cluster]);

  useEffect(() => {
    if (search.trim()) {
      let filteredData = allData?.studentDetails?.filter((item: any) =>
        item?.student_Name?.toLowerCase().includes(search.toLowerCase()),
      );
      setTotalRecords(filteredData?.length);
      setItemsPerPage(filteredData?.length);
      setData(filteredData);
      return;
    }
    setTotalRecords(allData?.studentDetails?.length);
    setItemsPerPage(DEFAULT_ITEMS_PER_PAGE);
    let data = allData?.studentDetails?.slice(
      currentPage * DEFAULT_ITEMS_PER_PAGE,
      currentPage * DEFAULT_ITEMS_PER_PAGE + DEFAULT_ITEMS_PER_PAGE,
    );
    setData(data);
  }, [search, allData, currentPage]);
  return {
    district,
    setDistrict,
    block,
    setBlock,
    cluster,
    setCluster,
    school,
    setSchool,
    districts,
    blocks,
    classes,
    schools,
    clusters,
    user,
    role,
    status,
    setStatus,
    grade,
    setGrade,
    date,
    setDate,
    loading,
    getAttendanceData,
    data,
    allData,
    search,
    setSearch,
    currentPage,
    setCurrentPage,
    itemsPerPage,
    totalRecords,
    nextPage,
    prevPage,
  };
};

export default useDailyStudentAttendance;
